import { useEffect, useRef, useState, type ReactNode } from "react";
import { X } from "@phosphor-icons/react";

import { defaultIconProps } from "../features/shared/ui/icons";
import { useDismissableNotice } from "../hooks/useDismissableNotice";
import { useSwipeToDismiss } from "../hooks/useSwipeToDismiss";

interface InfoBannerProps {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  className?: string;
  children?: ReactNode;
  tone?: "neutral" | "warning" | "success" | "info";
  iconSize?: "md" | "lg";
  prominent?: boolean;
  dismissId?: string;
  dismissDurationS?: number;
  dismissLabel?: string;
}

const baseClasses =
  "relative flex w-full max-w-full items-start gap-4 rounded-2xl border p-4 text-slate-900 shadow-sm shadow-slate-200/60 ring-1 transition-colors transition-transform transition-opacity duration-200 ease-out dark:text-slate-100";
const toneClasses: Record<
  NonNullable<InfoBannerProps["tone"]>,
  { container: string; icon: string }
> = {
  neutral: {
    container:
      "border-slate-200/80 bg-slate-50/80 ring-slate-100 dark:border-slate-800 dark:bg-slate-800/60 dark:ring-slate-800",
    icon: "bg-white text-slate-600 ring-1 ring-slate-100 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700",
  },
  warning: {
    container:
      "border-amber-200 bg-amber-50 text-amber-900 ring-amber-100 shadow-md shadow-amber-100/50 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-50 dark:ring-amber-900/60",
    icon: "bg-white text-amber-700 ring-1 ring-amber-100 dark:bg-amber-900/40 dark:text-amber-100 dark:ring-amber-800",
  },
  success: {
    container:
      "border-emerald-200 bg-emerald-50 text-emerald-900 ring-emerald-100 shadow-md shadow-emerald-100/50 dark:border-emerald-900/50 dark:bg-emerald-900/20 dark:text-emerald-50 dark:ring-emerald-900/60",
    icon: "bg-white text-emerald-700 ring-1 ring-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-100 dark:ring-emerald-800",
  },
  info: {
    container:
      "border-sky-200 bg-gradient-to-r from-sky-50 via-white to-indigo-50 text-slate-900 ring-sky-100 shadow-md shadow-sky-100/50 dark:border-sky-800 dark:bg-gradient-to-r dark:from-sky-950/60 dark:via-slate-900/80 dark:to-indigo-950/60 dark:text-sky-50 dark:ring-sky-900/70 dark:shadow-sky-900/30",
    icon: "bg-white text-sky-700 ring-1 ring-sky-100 dark:bg-sky-950 dark:text-sky-100 dark:ring-sky-900",
  },
};
const iconWrapperClassesBase = "flex items-center justify-center rounded-xl aspect-square";
const iconSizeClasses: Record<NonNullable<InfoBannerProps["iconSize"]>, string> = {
  md: "h-12 w-12 text-lg md:h-14 md:w-14 md:text-xl lg:h-16 lg:w-16 lg:text-2xl",
  lg: "h-14 w-14 text-xl md:h-16 md:w-16 md:text-2xl lg:h-18 lg:w-18 lg:text-3xl",
};
const contentClasses = "flex flex-col gap-2 text-left min-w-0";
const titleClasses =
  "text-base font-semibold leading-6 text-slate-900 dark:text-slate-100 sm:text-lg";
const descriptionClasses =
  "text-sm leading-relaxed text-slate-600 dark:text-slate-400 sm:text-base sm:leading-relaxed";
const actionsClasses =
  "flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2";

export function InfoBanner({
  icon,
  title,
  description,
  className,
  children,
  tone = "neutral",
  iconSize = "md",
  prominent = false,
  dismissId,
  dismissDurationS,
  dismissLabel,
}: InfoBannerProps) {
  const { hidden, dismiss } = useDismissableNotice({
    id: dismissId,
    durationSeconds: dismissDurationS,
  });
  const [isClosing, setIsClosing] = useState(false);
  const closeTimeoutRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
    }
  }, []);

  const handleDismiss = () => {
    if (isClosing || hidden) {
      return;
    }
    setIsClosing(true);
    closeTimeoutRef.current = window.setTimeout(() => {
      dismiss();
    }, 160);
  };

  const { swipeHandlers, swipeStyle, isSwiping } = useSwipeToDismiss({
    onDismiss: handleDismiss,
    enabled: Boolean(dismissId),
  });
  const effectiveStyle = isClosing ? undefined : swipeStyle;

  if (hidden) {
    return null;
  }

  const toneClass = toneClasses[tone];
  const iconClasses = [
    iconWrapperClassesBase,
    iconSizeClasses[iconSize],
    toneClass.icon,
  ]
    .filter(Boolean)
    .join(" ");
  const containerClasses = [
    baseClasses,
    toneClass.container,
    dismissId ? "cursor-grab active:cursor-grabbing touch-pan-y" : "",
    isSwiping ? "select-none" : "",
    isSwiping ? "transition-none" : "",
    isClosing ? "opacity-0 translate-y-1" : "opacity-100",
    prominent ? "p-5 sm:p-6 gap-5" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const dismissText = dismissLabel ?? "Dismiss";
  const contentClassNames = [
    contentClasses,
    dismissId ? "pr-12 sm:pr-14" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const renderDescription = () => {
    if (!description) {
      return null;
    }
    if (typeof description === "string") {
      return <p className={descriptionClasses}>{description}</p>;
    }
    return <div className={descriptionClasses}>{description}</div>;
  };

  return (
    <div
      className={containerClasses}
      style={effectiveStyle}
      {...swipeHandlers}
    >
      {dismissId ? (
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute right-4 top-4 inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 sm:right-5 sm:top-5"
          aria-label={dismissText}
          data-swipe-dismiss-ignore
          onPointerDown={(event) => event.stopPropagation()}
          onPointerUp={(event) => event.stopPropagation()}
        >
          <X {...defaultIconProps} aria-hidden />
        </button>
      ) : null}
      {icon ? (
        <div className={iconClasses} aria-hidden>
          {icon}
        </div>
      ) : null}
      <div className={contentClassNames}>
        <div className="flex items-start gap-3">
          <p className={titleClasses}>{title}</p>
        </div>
        {renderDescription()}
        {children ? <div className={actionsClasses}>{children}</div> : null}
      </div>
    </div>
  );
}
